import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { DollarSign, Lock, Shield, Sparkles, CreditCard, Tag, X, Check } from "lucide-react";
import SakuraDivider from "@/components/SakuraDivider";
import { api } from "@/lib/api";

export default function Pay() {
  const [params] = useSearchParams();
  const inquiryId = params.get("inquiry") || "";
  const suggested = params.get("amount") || "";
  const canceled = params.get("canceled") === "1";

  const [cfg, setCfg] = useState({ min: 15, max: 2000, presets: [15, 25, 50, 100], currency: "usd" });
  const [amount, setAmount] = useState(suggested || "");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const [codeInput, setCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState(null); // {code, amount_off, remaining_uses}
  const [checkingCode, setCheckingCode] = useState(false);

  useEffect(() => {
    api.get("/payments/config").then(({ data }) => setCfg(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (canceled) toast.info("Payment canceled. You can try again below.");
  }, [canceled]);

  const numeric = useMemo(() => {
    const n = parseFloat(amount);
    return Number.isFinite(n) ? n : 0;
  }, [amount]);

  const discountOff = appliedCode?.amount_off || 0;
  const finalAmount = Math.max(0, numeric - discountOff);
  const valid = numeric >= cfg.min && numeric <= cfg.max && finalAmount >= 1;

  const applyCode = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    setCheckingCode(true);
    try {
      const { data } = await api.get(`/discount/${encodeURIComponent(code)}`);
      if (data.valid) {
        setAppliedCode(data);
        toast.success(`Code applied — $${data.amount_off.toFixed(2)} off!`);
      } else {
        const reasons = {
          not_found: "Code not found",
          inactive: "This code is no longer active",
          exhausted: "This code has been fully redeemed",
          empty: "Please enter a code",
        };
        toast.error(reasons[data.reason] || "Code is not valid");
      }
    } catch (err) {
      toast.error("Could not validate code");
    } finally {
      setCheckingCode(false);
    }
  };

  const removeCode = () => {
    setAppliedCode(null);
    setCodeInput("");
  };

  const pay = async () => {
    if (!valid) {
      toast.error(`Enter an amount between $${cfg.min} and $${cfg.max}`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/stripe/session", {
        origin_url: window.location.origin,
        amount: Math.round(numeric * 100) / 100,
        inquiry_id: inquiryId,
        name, email,
        discount_code: appliedCode?.code || "",
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Payment error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="page-pay">
      <section className="section">
        <div className="mx-auto max-w-3xl text-center">
          <span className="chip"><CreditCard className="h-3 w-3" /> Secure Deposit</span>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-sakura-ink">Pay Your Deposit</h1>
          <p className="mt-4 text-sakura-soft max-w-xl mx-auto">
            Once we've confirmed your details over email, pay a deposit to lock in your date. Payments are processed securely by Stripe.
          </p>
          <SakuraDivider />
        </div>
      </section>

      <section className="section pt-0">
        <div className="mx-auto max-w-xl card-soft p-6 md:p-10" data-testid="pay-card">
          {inquiryId && (
            <div className="rounded-2xl bg-sakura-100 text-sakura-ink text-sm px-4 py-3 mb-5">
              Applying deposit to inquiry <b>#{inquiryId.slice(0, 8)}</b>
            </div>
          )}

          <label className="block">
            <span className="text-sm font-semibold text-sakura-ink flex items-center gap-2"><DollarSign className="h-4 w-4 text-sakura-500" /> Amount (USD)</span>
            <input
              type="number" min={cfg.min} max={cfg.max} step="1"
              className="input-soft mt-1 text-lg"
              placeholder={`Between $${cfg.min} and $${cfg.max}`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="pay-amount"
            />
          </label>

          <div className="mt-4 flex flex-wrap gap-2" data-testid="pay-presets">
            {cfg.presets.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setAmount(String(p))}
                className={`px-4 py-2 rounded-full text-sm font-semibold border transition ${
                  numeric === p ? "bg-sakura-400 text-white border-sakura-400 shadow-sakura" : "bg-white text-sakura-ink border-sakura-200 hover:bg-sakura-100"
                }`}
                data-testid={`pay-preset-${p}`}
              >
                ${p}
              </button>
            ))}
          </div>

          {/* Discount code */}
          <div className="mt-6 rounded-2xl bg-sakura-50 border border-sakura-200 p-4" data-testid="discount-section">
            <div className="text-sm font-semibold text-sakura-ink flex items-center gap-2 mb-2">
              <Tag className="h-4 w-4 text-sakura-500" /> Have a discount code?
            </div>
            {appliedCode ? (
              <div className="flex items-center justify-between gap-3 rounded-xl bg-white border border-sakura-200 px-3 py-2.5">
                <div className="flex items-center gap-2 min-w-0">
                  <Check className="h-4 w-4 text-emerald-500 shrink-0" />
                  <span className="text-sm text-sakura-ink font-semibold truncate">{appliedCode.code}</span>
                  <span className="text-xs text-sakura-soft">−${appliedCode.amount_off.toFixed(2)}</span>
                </div>
                <button onClick={removeCode} className="h-7 w-7 grid place-items-center rounded-full bg-sakura-100 text-sakura-500 hover:bg-sakura-200" aria-label="Remove code" data-testid="discount-remove">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  className="input-soft flex-1 uppercase tracking-wide"
                  placeholder="e.g., FIRST10"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyCode(); } }}
                  data-testid="discount-input"
                />
                <button
                  type="button"
                  onClick={applyCode}
                  disabled={!codeInput.trim() || checkingCode}
                  className="btn-secondary text-sm disabled:opacity-60"
                  data-testid="discount-apply"
                >
                  {checkingCode ? "…" : "Apply"}
                </button>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-5">
            <label className="block">
              <span className="text-sm font-semibold text-sakura-ink">Name</span>
              <input className="input-soft mt-1" value={name} onChange={(e) => setName(e.target.value)} data-testid="pay-name" placeholder="Optional" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-sakura-ink">Email</span>
              <input className="input-soft mt-1" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="pay-email" placeholder="Optional" />
            </label>
          </div>

          {/* Total summary */}
          {numeric > 0 && (
            <div className="mt-6 rounded-2xl bg-white border border-sakura-200 p-4 space-y-1.5 text-sm" data-testid="pay-summary">
              <div className="flex justify-between text-sakura-soft"><span>Subtotal</span><span>${numeric.toFixed(2)}</span></div>
              {discountOff > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span>Discount ({appliedCode.code})</span>
                  <span>−${discountOff.toFixed(2)}</span>
                </div>
              )}
              <div className="border-t border-sakura-200 my-2" />
              <div className="flex justify-between font-serif text-xl text-sakura-ink">
                <span>Total</span>
                <span data-testid="pay-total">${finalAmount.toFixed(2)}</span>
              </div>
            </div>
          )}

          <button
            onClick={pay}
            disabled={!valid || busy}
            className="btn-primary mt-6 w-full disabled:opacity-60"
            data-testid="pay-submit"
          >
            <Lock className="h-4 w-4" /> {busy ? "Redirecting…" : `Pay $${valid ? finalAmount.toFixed(2) : "—"} securely`}
          </button>

          <p className="mt-4 text-xs text-sakura-soft text-center flex items-center justify-center gap-2">
            <Shield className="h-3.5 w-3.5 text-sakura-400" /> Secured by Stripe · no card data touches our site
          </p>
          <p className="mt-2 text-[11px] text-sakura-soft text-center">
            Deposits are non-refundable — see our <Link to="/terms" className="text-sakura-500 underline">Terms</Link>.
          </p>
        </div>

        <div className="max-w-xl mx-auto mt-6 text-center text-sm text-sakura-soft">
          Haven't submitted an inquiry yet? <Link to="/custom-orders" className="text-sakura-500 font-semibold hover:underline">Start one here</Link>.
        </div>
      </section>
    </div>
  );
}
